import type { ZodType } from "zod";

import { cleanCompanyData, cleanPlantingData } from "./cleaner.js";
import type { CleanedCompanyData, CleanedPlantingData } from "./types/index.js";

import { companyDataSchema, ORGID, plantingDataSchema } from "./index.js";

type APIResponse = {
	companyData: CleanedCompanyData;
	plantingData: CleanedPlantingData;
};

/**
 * Get all API data
 *
 * @param orgId The organization ID to get data for (defaults to us).
 * @param fetcher The function to use to fetch the data. Defaults to the API
 *   fetcher.
 * @returns The cleaned company and planting data
 */
export async function getAPIData(
	orgId: string = ORGID,
	fetcher = queryAPI,
): Promise<APIResponse> {
	const a = fetcher(
		`orgs/pstats?org_id=${orgId}&org_type=sponsorAccount&_v=25.0.0`,
		companyDataSchema,
	).then((data) => {
		return cleanCompanyData(data);
	});
	const b = fetcher(
		`sponsors/${orgId}/map-data?_v=25.0.0`,
		plantingDataSchema,
	).then((data) => {
		return cleanPlantingData(data);
	});

	return {
		companyData: await a,
		plantingData: await b,
	};
}

/**
 * Query the API and extract the JSON response.
 *
 * @template T The type of the response data.
 * @param url The endpoint to query
 * @param schema The schema to validate the response against
 * @returns The JSON response
 * @throws Error if the response is not OK
 */
async function queryAPI<T>(url: string, schema: ZodType<T>): Promise<T> {
	const response = await fetch(`https://api.veritree.com/api/${url}`);
	if (!response.ok)
		throw new Error(
			`Error getting API data from ${url}: ${response.statusText}`,
		);
	const data = await response.json();
	return schema.parse(data);
}
